import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';
import CartDrawer from '../components/common/CartDrawer';
import api from '../utils/api';
import toast from 'react-hot-toast';

export default function PriceListPage() {
  const [lists,   setLists]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    api.get('/pricelists')
      .then(r => setLists(r.data.data || []))
      .finally(() => setLoading(false));
  }, []);

  // The <a download> attribute doesn't reliably work for cross-origin URLs
  // (our PDFs live on Cloudinary, a different domain) — the browser was
  // saving the raw file with no extension and a random filename instead.
  // Fetching it as a blob first lets us force a real filename + .pdf type.
  const handleDownload = async (pl) => {
    setDownloadingId(pl._id);
    try {
      const res = await fetch(pl.url);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      const filename = pl.filename?.toLowerCase().endsWith('.pdf')
        ? pl.filename
        : `${pl.title || 'price-list'}.pdf`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      toast.error('Download failed — please try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <CartDrawer />

      <main className="flex-1 pt-20">
        {/* Header */}
        <div className="bg-on-surface text-white px-6 md:px-10 py-8">
          <div className="max-w-screen-2xl mx-auto">
            <div className="flex items-center gap-2 font-label text-xs text-gray-500 uppercase tracking-widest mb-3">
              <Link to="/" className="hover:text-gray-300">Home</Link>
              <span>/</span>
              <span className="text-gray-400">Price List</span>
            </div>
            <div className="max-w-2xl">
              <div className="accent-bar" />
              <h1 className="font-headline  text-5xl md:text-6xl uppercase tracking-tight text-white">
                Price Lists
              </h1>
              <p className="font-body text-gray-400 mt-4 leading-relaxed">
                Download our latest price lists in PDF format. Contact us for custom pricing or bulk orders.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-screen-2xl mx-auto px-6 md:px-10 py-16">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => <div key={i} className="h-40 skeleton" />)}
            </div>
          ) : lists.length === 0 ? (
            <div className="text-center py-24">
              <span className="material-symbols-outlined text-[80px] text-outline-variant">picture_as_pdf</span>
              <h2 className="font-headline font-black text-3xl uppercase mt-4 text-secondary">No price lists available</h2>
              <p className="font-body text-sm text-secondary mt-2 mb-8">Price lists will be posted here once available.</p>
              <Link to="/feedback" className="btn-primary">Contact Us for Pricing</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {lists.map(pl => (
                <div key={pl._id} className="bg-white border border-outline-variant/20 card-industrial flex flex-col">
                  {/* PDF Icon area */}
                  <div className="bg-surface-container-low border-b border-outline-variant/20 px-6 py-8 flex items-center gap-4">
                    <div className="w-14 h-14 bg-red-100 flex items-center justify-center flex-shrink-0">
                      <span className="material-symbols-outlined text-[32px] text-red-600">picture_as_pdf</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-headline font-bold text-lg uppercase tracking-tight text-on-surface leading-tight truncate">{pl.title}</p>
                      <p className="font-label text-xs text-secondary mt-0.5 truncate">{pl.filename}</p>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="px-6 py-5 flex-1 flex flex-col">
                    {pl.description && (
                      <p className="font-body text-sm text-secondary mb-4 leading-relaxed">{pl.description}</p>
                    )}
                    <div className="mt-auto flex items-center justify-between">
                      <span className="font-label text-xs text-secondary">
                        Updated {new Date(pl.updatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                      <button onClick={() => handleDownload(pl)} disabled={downloadingId === pl._id}
                        className="flex items-center gap-2 px-4 py-2 bg-primary-container text-white font-label text-xs font-bold uppercase tracking-wider hover:bg-primary transition-colors disabled:opacity-60">
                        <span className="material-symbols-outlined text-[16px]">
                          {downloadingId === pl._id ? 'hourglass_empty' : 'download'}
                        </span>
                        {downloadingId === pl._id ? 'Downloading...' : 'Download'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Info banner */}
          <div className="mt-16 bg-surface-container-low border border-outline-variant/20 p-8 flex flex-col md:flex-row items-center gap-6">
            <span className="material-symbols-outlined text-[48px] text-primary-container flex-shrink-0">info</span>
            <div>
              <h3 className="font-headline font-bold text-xl uppercase tracking-tight text-on-surface">Need Custom Pricing?</h3>
              <p className="font-body text-sm text-secondary mt-1 leading-relaxed">
                For bulk orders, special grades, or custom specifications — contact us directly. We offer competitive pricing for large volume orders.
              </p>
            </div>
            <Link to="/feedback" className="btn-primary whitespace-nowrap flex-shrink-0">
              <span className="material-symbols-outlined">mail</span>
              Get in Touch
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}